import { Box, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompactMoney, formatCurrency, latest, previous } from '../../safeT/types';
import { getRevenueBenchmarks } from '../../safeT/benchmarks';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { CHART_GOAL_LINE_COLOR, CHART_GOAL_LINE_PROPS } from '../../constants';
import { roundedStackBarTopShape, stackBarTopShape, type BarShapeProps } from '../../rechartsStackedBarShapes';
import { parseQuarter, pctChange } from '../componentProjectMetricUtils';
import {
  hasSafeTRevenueSubawardeeBreakdown,
  type SafeTComponentProjectData,
  type SafeTComponentProjectPoint,
} from './mapSafeTComponentProjectData';

const SUBAWARDEE_COLORS = ['#339AF0', '#51CF66', '#FAB005', '#845EF7', '#22B8CF'];
const TOTAL_REVENUE_KEY = 'revenue';

type SafeTRevenueBySubawardeeCardProps = {
  data: SafeTComponentProjectData;
};

function getPointRevenue(point: SafeTComponentProjectPoint): number {
  if (point.revenueBySubawardee && Object.keys(point.revenueBySubawardee).length > 0) {
    return Object.values(point.revenueBySubawardee).reduce((sum, value) => sum + value, 0);
  }
  return point.revenue;
}

function getActiveStackKeys(data: SafeTComponentProjectData): string[] {
  if (hasSafeTRevenueSubawardeeBreakdown(data)) {
    const active = new Set<string>();
    for (const point of data.series) {
      for (const [subawardeeId, revenue] of Object.entries(point.revenueBySubawardee ?? {})) {
        if (revenue > 0) active.add(subawardeeId);
      }
    }
    if (active.size > 0) {
      return data.revenueSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
    }
    return data.revenueSubawardeeIds;
  }
  return [TOTAL_REVENUE_KEY];
}

export function SafeTRevenueBySubawardeeCard({ data }: SafeTRevenueBySubawardeeCardProps) {
  const theme = useMantineTheme();
  const isStackedBySubawardee = hasSafeTRevenueSubawardeeBreakdown(data);
  const stackKeys = getActiveStackKeys(data);
  const series = data.series;
  const cur = latest(series);
  const prev = previous(series);
  const subtitle = cur?.quarter ?? 'No data';

  const curTotal = cur ? getPointRevenue(cur) : null;
  const prevTotal = prev ? getPointRevenue(prev) : null;
  const qoq = curTotal !== null ? pctChange(curTotal, prevTotal) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText =
    qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + getPointRevenue(point), 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + getPointRevenue(point), 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText =
    yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const allTimeTotal = data.allTime.revenue;

  const annualGoal =
    cur?.goalTargets?.revenue ??
    getRevenueBenchmarks({ mode: 'combined' }).annualRevenueGoal ??
    null;

  const annualChartData = (() => {
    const byYear = new Map<number, Record<string, number | string>>();

    for (const point of series) {
      const meta = parseQuarter(point.quarter);
      if (!meta) continue;

      const row = byYear.get(meta.year) ?? {
        year: meta.year,
        yearLabel: String(meta.year),
      };

      if (isStackedBySubawardee) {
        for (const stackKey of stackKeys) {
          const revenue = point.revenueBySubawardee?.[stackKey] ?? 0;
          row[stackKey] = Number(row[stackKey] ?? 0) + revenue;
        }
      } else {
        row[TOTAL_REVENUE_KEY] = Number(row[TOTAL_REVENUE_KEY] ?? 0) + point.revenue;
      }

      byYear.set(meta.year, row);
    }

    return Array.from(byYear.values()).sort((a, b) => Number(a.year) - Number(b.year));
  })();

  const yMax = Math.max(
    0,
    ...annualChartData.flatMap((row) => stackKeys.map((stackKey) => Number(row[stackKey] ?? 0))),
    annualGoal ?? 0
  );
  const yDomain: [number, number] = [0, yMax <= 0 ? 1 : Math.ceil(yMax * 1.12)];

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const stackLabels: Record<string, string> = isStackedBySubawardee
    ? Object.fromEntries(stackKeys.map((key) => [key, key]))
    : { [TOTAL_REVENUE_KEY]: 'Revenue' };

  return (
    <CardShell title="Revenue Generated" subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Total revenue generated this quarter across all SAFE-T sub-awardees.',
              value: (
                <Text size="lg" weight={800}>
                  {curTotal === null ? '—' : formatCurrency(curTotal)}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year-to-date revenue total across all SAFE-T sub-awardees.',
              value: (
                <Text size="lg" weight={800}>
                  {formatCurrency(ytd)}
                </Text>
              ),
              sublabel: (
                <>
                  {yoyText}
                  {annualGoal !== null ? (
                    <>
                      <br />
                      {`Goal ${formatCurrency(annualGoal)}/year`}
                    </>
                  ) : null}
                </>
              ),
            },
            {
              label: 'All-Time',
              labelTooltip: 'Revenue across all reported quarters for all SAFE-T sub-awardees.',
              value: (
                <Text size="lg" weight={800}>
                  {allTimeTotal > 0 ? formatCurrency(allTimeTotal) : '—'}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          {isStackedBySubawardee ? 'Annual Trend by Sub-awardee' : 'Annual Trend'}
        </Text>

        <Box sx={{ height: 210 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualChartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={52}
                tickMargin={8}
                domain={yDomain}
                tickFormatter={(value) => formatCompactMoney(Number(value))}
              />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const total = payload.reduce(
                    (sum, entry) => sum + Number(entry.value ?? 0),
                    0
                  );
                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      {isStackedBySubawardee
                        ? payload.map((entry) => (
                            <div
                              key={String(entry.dataKey)}
                              style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}
                            >
                              {entry.name}: {formatCurrency(Number(entry.value ?? 0))}
                            </div>
                          ))
                        : (
                            <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>
                              Revenue: {formatCurrency(total)}
                            </div>
                          )}
                      {isStackedBySubawardee ? (
                        <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6 }}>
                          Total: {formatCurrency(total)}
                        </div>
                      ) : null}
                      {annualGoal !== null ? (
                        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 6 }}>
                          Goal {formatCurrency(annualGoal)}
                        </div>
                      ) : null}
                    </div>
                  );
                }}
              />
              {annualGoal !== null ? (
                <ReferenceLine
                  y={annualGoal}
                  stroke={CHART_GOAL_LINE_COLOR}
                  strokeWidth={CHART_GOAL_LINE_PROPS.strokeWidth}
                  strokeDasharray={CHART_GOAL_LINE_PROPS.strokeDasharray}
                  ifOverflow="extendDomain"
                />
              ) : null}
              {stackKeys.map((stackKey, index) => (
                <Bar
                  key={stackKey}
                  dataKey={stackKey}
                  name={stackLabels[stackKey] ?? stackKey}
                  stackId={isStackedBySubawardee ? 'revenue' : undefined}
                  fill={SUBAWARDEE_COLORS[index % SUBAWARDEE_COLORS.length]}
                  shape={
                    isStackedBySubawardee
                      ? stackBarTopShape(stackKeys, stackKey)
                      : (props: BarShapeProps) => roundedStackBarTopShape(props, true)
                  }
                  barSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {isStackedBySubawardee ? (
          <Group position="center" spacing="lg" mt={4}>
            {stackKeys.map((stackKey, index) => (
              <Group key={stackKey} spacing={6} noWrap>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: SUBAWARDEE_COLORS[index % SUBAWARDEE_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" color="dimmed">
                  {stackKey}
                </Text>
              </Group>
            ))}
          </Group>
        ) : null}
      </Stack>
    </CardShell>
  );
}
